/**
 * Curated content for the Keystone `/roadmap` page.
 *
 * This module is the single source of truth for the roadmap's copy and
 * visuals. It is pure data — no JSX, no React import, no data fetching —
 * so both the authed page (R2 of this spec) and a future public route can
 * consume it unchanged.
 *
 * Freshness: this content does NOT auto-update from epics. When shipping a
 * new client-visible feature, add a new item here (and a matching visual
 * under `./assets/`) — see R5's freshness process hook for the reminder
 * mechanism (PR-template checklist + release-process doc step).
 *
 * Visual grammar: a real product screenshot ("ui") means the feature is
 * shipped and live; a generated illustration ("art") means it is coming OR a
 * shipped feature whose real screenshot is a pending swap. Three shipped tiles
 * (proposals, invoicing, bill-to) use real UI; the AI connector and field-review
 * tiles use illustrations (the connector's only real view is an unconfigured
 * empty state; field-review real data is seed-only in the test tenant).
 */

import heroArt from './assets/hero.png'
import aiConnectorArt from './assets/ai-connector.png'
import proposalsDetailShot from './assets/proposals-detail.png'
import invoicingDetailShot from './assets/invoicing-detail.png'
import billToDetailShot from './assets/bill-to-detail.png'
import fieldReviewArt from './assets/field-review.png'
import accountingSyncArt from './assets/accounting-sync.png'
import complianceArt from './assets/compliance-check.png'
import clientPortalArt from './assets/client-portal.png'
import guidedFormsArt from './assets/guided-forms.png'
import caTrackerArt from './assets/ca-tracker.png'

/** Shipping status of a roadmap item — drives node styling + filter bucket. */
export type RoadmapStatus = 'shipped' | 'building' | 'horizon'

/**
 * Visual grammar: a real product screenshot ("ui") means the feature is
 * shipped and live; a generated illustration ("art") means it is coming.
 */
export type RoadmapVisualKind = 'ui' | 'art'

export interface RoadmapVisual {
  kind: RoadmapVisualKind
  src: string
  alt: string
}

export interface RoadmapItem {
  id: string
  category: string
  title: string
  hook: string
  desc: string
  status: RoadmapStatus
  visual: RoadmapVisual
}

export interface RoadmapHero {
  eyebrow: string
  title: string
  lede: string
  chip: string
}

export interface RoadmapTieback {
  heading: string
  body: string
  ctaLabel: string
}

export const roadmapHero: RoadmapHero = {
  eyebrow: 'Product roadmap',
  title: 'What we’re building',
  lede:
    'A live look at where Keystone’s headed. What we’ve shipped lately, what we’re building now, and where we’re pointed next. A lot of it starts as feedback from the firms using it every day.',
  chip: 'Updated this week',
}

export const roadmapTieback: RoadmapTieback = {
  heading: 'See something missing?',
  body:
    'A lot of what’s here started as someone’s feedback. If there’s something that’d make Keystone better for your work, tell us.',
  ctaLabel: 'Report feedback →',
}

export const roadmapShipped: RoadmapItem[] = [
  {
    id: 'ai-connector',
    category: 'Keystone / AI',
    title: 'Run Keystone by talking to it',
    hook: 'Connect your AI assistant and drive Keystone in plain language.',
    desc: "Draft proposals, pull up any project or invoice, or hand it a call transcript and get a proposal draft back, just by asking. Secure per-person access you grant or revoke anytime.",
    status: 'shipped',
    visual: {
      kind: 'art',
      src: aiConnectorArt,
      alt: 'Illustration of an AI assistant connected to the Keystone app, driving it in plain language',
    },
  },
  {
    id: 'proposals',
    category: 'Proposals / Fees',
    title: 'Proposals built from your own fee library',
    hook: 'Start from your roles and rates, not a blank page.',
    desc: "Reuse your standard fee assumptions, send a branded PDF, and turn a won proposal into a live project in one click.",
    status: 'shipped',
    visual: {
      kind: 'ui',
      src: proposalsDetailShot,
      alt: "Keystone proposal detail screen showing a fee table with proposed line items and totals",
    },
  },
  {
    id: 'invoicing',
    category: 'Billing / Invoices',
    title: 'Invoicing that tracks itself',
    hook: "Bill by milestone and see what's still owed at a glance.",
    desc: "Record partial payments, keep a full payment history, and always know your outstanding balance without a spreadsheet.",
    status: 'shipped',
    visual: {
      kind: 'ui',
      src: invoicingDetailShot,
      alt: "Keystone invoice detail screen showing payment status and a partial-payment progress bar",
    },
  },
  {
    id: 'bill-to',
    category: 'Billing / Bill-to',
    title: 'Set who each invoice bills to, once',
    hook: 'Fix a legal name, DBA, or address one time.',
    desc: "Correct the bill-to on an invoice and choose whether it applies to just that one or every invoice going forward. The monthly re-fixing loop is gone.",
    status: 'shipped',
    visual: {
      kind: 'ui',
      src: billToDetailShot,
      alt: "Keystone invoice bill-to panel showing client, legal name, DBA, billing address, and recipient controls",
    },
  },
  {
    id: 'field-review',
    category: 'Field / Site visits',
    title: 'Field reviews that come back as a finished report',
    hook: 'Walk the site, get a report your client can trust.',
    desc: "Site-visit reports in your branding with photo grids, auto-numbered FR identifiers, and the reliance language on every page.",
    status: 'shipped',
    visual: {
      kind: 'art',
      src: fieldReviewArt,
      alt: 'Illustration of site photos from a phone flowing into a professional field-review report',
    },
  },
]

export const roadmapBuilding: RoadmapItem[] = [
  {
    id: 'accounting',
    category: 'Billing / Accounting',
    title: 'Invoices that land in QuickBooks on their own',
    hook: 'Bill in Keystone, see it in your books.',
    desc: "Push invoices to QuickBooks Online with the right customers and tax codes, and approve each one before it syncs. No re-keying the same numbers into two systems.",
    status: 'building',
    visual: {
      kind: 'art',
      src: accountingSyncArt,
      alt: 'Illustration of invoices syncing from Keystone into QuickBooks',
    },
  },
  {
    id: 'compliance',
    category: 'Drawings / Compliance',
    title: 'Automated code checks on your drawings',
    hook: 'Catch the obvious issues before permit review does.',
    desc: "Point Keystone at a drawing set and get an automated first pass against building code, so nothing simple slips through. In beta now.",
    status: 'building',
    visual: {
      kind: 'art',
      src: complianceArt,
      alt: 'Illustration of a drawing set being automatically checked against building code',
    },
  },
  {
    id: 'client-portal',
    category: 'Clients / Portal',
    title: 'A live portal for every client',
    hook: 'Give clients a real window into their project.',
    desc: "A branded, read-only portal where your client sees where things stand, what's outstanding, and their invoices, all in one place.",
    status: 'building',
    visual: {
      kind: 'art',
      src: clientPortalArt,
      alt: 'Illustration of a client-facing project portal showing status cards and documents',
    },
  },
]

export const roadmapHorizon: RoadmapItem[] = [
  {
    id: 'guided-forms',
    category: 'Clients / Intake',
    title: "The owner's authorization form, filled in right the first time",
    hook: 'The form that never gets filled out correctly, handled.',
    desc: "A guided, one-field-at-a-time client form that validates as it goes, routes signatures across every party, and drops the details straight into your contacts and billing. No more chasing a form back three times.",
    status: 'horizon',
    visual: {
      kind: 'art',
      src: guidedFormsArt,
      alt: 'Illustration of a guided client form that validates, routes signatures, and fills records automatically',
    },
  },
  {
    id: 'ca',
    category: 'Construction admin',
    title: 'Your CA phase runs itself, and bills itself',
    hook: 'Run construction admin as a connected workflow.',
    desc: "Each site review spawns the next checklist, gates to close-out, and the construction-administration invoices draft themselves as each phase completes.",
    status: 'horizon',
    visual: {
      kind: 'art',
      src: caTrackerArt,
      alt: 'Illustration of a construction-administration workflow moving deliverables through to close-out',
    },
  },
]

/** All curated roadmap items, in order (shipped, then building, then horizon). */
export const roadmapItems: RoadmapItem[] = [
  ...roadmapShipped,
  ...roadmapBuilding,
  ...roadmapHorizon,
]

/** Hero illustration — separate from the tile items, used at the top of the page. */
export const roadmapHeroVisual: RoadmapVisual = {
  kind: 'art',
  src: heroArt,
  alt: 'A building under construction rising from a blueprint',
}
